import { ActionDropdown, Strong, Tag } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { showTransientNotification } from '@driftt/traffic-control'
import moment from 'moment'
import * as R from 'ramda'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { savePlaybookStatus } from 'api/playbooks'
import { TIMEZONE_TO_ABBR } from 'constants/timezones'
import { isMigratedPlaybook } from 'utils/playbooks'
import {
  trackDefaultPlaybookStatusToggled,
  trackPlaybookStatusToggled,
} from 'utils/tracking'

import DropdownTrigger from './DropdownTrigger'

import './style.css'

const Status = {
  Enabled: 'ENABLED',
  Disabled: 'DISABLED',
  Archived: 'ARCHIVED',
}

const PlaybookVersionSelect = ({
  publishedPlaybook,
  onUpdateStatus,
  scheduledTask,
  onDeleteTask,
  onShowSchedulerModal,
  onShowVersionHistoryModal,
}) => {
  const dispatch = useDispatch()
  const defaultPlaybookName = useSelector(
    R.path(['__PLAYBOOK_EDITOR', 'playbook', 'meta', 'defaultPlaybook'])
  )

  const [isSaving, setIsSaving] = useState(false)

  const isPlaybookEnabled = publishedPlaybook.status === Status.Enabled
  const isPlaybookArchived = publishedPlaybook.status === Status.Archived
  const isExperimentEnabled = !!publishedPlaybook.experiment?.enabled

  const handleTogglePlaybook = async (hideDropdown) => {
    setIsSaving(true)
    hideDropdown()

    const { id } = publishedPlaybook
    const status = isPlaybookEnabled ? Status.Disabled : Status.Enabled

    if (scheduledTask) {
      onDeleteTask(scheduledTask.action)
    }

    try {
      const { data } = await savePlaybookStatus({ id, status })

      onUpdateStatus(data)

      dispatch(
        showTransientNotification({
          type: 'success',
          message: `Your playbook has been ${status.toLowerCase()}`,
        })
      )

      if (defaultPlaybookName) {
        trackDefaultPlaybookStatusToggled(defaultPlaybookName, status)
      }

      trackPlaybookStatusToggled(status, 'header', id)
    } catch (err) {
      dispatch(
        showTransientNotification({
          type: 'error',
          message: 'Could not update playbook status',
        })
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleScheduler = (hideDropdown) => {
    onShowSchedulerModal()
    hideDropdown()
  }

  const handleShowVersionHistoryModal = (hideDropdown) => {
    onShowVersionHistoryModal()
    hideDropdown()
  }

  const dropdownActions = [
    {
      label: `Turn playbook ${isPlaybookEnabled ? 'off now' : 'on'}`,
      action: handleTogglePlaybook,
    },
  ]

  if (scheduledTask) {
    const disableNotice = `Scheduled to turn off on ${moment(
      scheduledTask.scheduledTime
    ).format('MM/DD/YY [at] h:mm A')} ${
      TIMEZONE_TO_ABBR[scheduledTask.timeZone]
    }`

    dropdownActions.unshift({
      label: (
        <div className="playbook-verion-select-dropdown-disable-notice">
          <Icon name="info" />
          <span>{disableNotice}</span>
        </div>
      ),
    })
  }

  if (isPlaybookEnabled) {
    dropdownActions.push({
      label: scheduledTask ? 'Edit scheduled date' : 'Turn playbook off later',
      action: handleScheduler,
    })
  }

  if (!isMigratedPlaybook(publishedPlaybook.reportType)) {
    dropdownActions.push({
      label: 'View version history',
      action: handleShowVersionHistoryModal,
    })
  }

  return (
    <div className="playbook-version-select-container">
      {isExperimentEnabled && isPlaybookEnabled && (
        <Tag
          text={<Strong>A/B TEST IS ON</Strong>}
          textColor="var(--color-p2-6)"
          backgroundColor="var(--color-p2-2)"
        />
      )}
      {isPlaybookArchived ? (
        <Tag
          wrapperClass="playbook-version-archived"
          type="default"
          text={'PLAYBOOK IS ARCHIVED'}
        />
      ) : (
        <ActionDropdown
          align="right"
          className="playbook-version-select-dropdown"
          trigger={
            <DropdownTrigger
              isPlaybookEnabled={isPlaybookEnabled}
              isSaving={isSaving}
            />
          }
          openTriggerClassName="playbook-version-select-dropdown--active"
          actions={dropdownActions}
        />
      )}
    </div>
  )
}

export default PlaybookVersionSelect
